<template>
  <VChart
    ref="chartElement"
    :option="option"
  />
</template>

<script setup lang="ts">
import type { ComponentsMainChartCircleFragment } from '#gql'

const props = withDefaults(defineProps<{
  chartData: {
    cells: string[]
  }[]
  unit: string | null
  unitPrefix: boolean
  segmentConfig: ComponentsMainChartCircleFragment['segment_config']
  showLabels?: boolean
  roseType?: 'radius' | null
}>(), {
  showLabels: true,
  roseType: null,
})

// Use localeProperties.iso if available, fallback to 'de-CH'
const { localeProperties } = useI18n()
const locale = localeProperties.value?.iso || 'de-CH'

const segmentConfigs = computed(() => {
  return props.segmentConfig
})

const chartElement = ref<null | HTMLDivElement>(null)

const dataRows = computed(() => {
  return props.chartData
})

const series = computed(() => {
  if (!Array.isArray(props.chartData) || props.chartData.length <= 1) return []

  return [
    {
      type: 'pie',
      roseType: props.roseType,
      radius: ['35%', '60%'],
      center: ['50%', '50%'], // Center the pie
      data: dataRows.value.map((row, index) => ({
        name: useFormatDate(row.cells[0], locale),
        value: row.cells.slice(1).reduce((sum, cell) => sum + parseFloat(cell || '0'), 0), // Sum if multiple columns; use row.cells[1] for single
        itemStyle: {
          color: segmentConfigs.value[index]?.segment_color, // fill color
        },
      })),
      label: {
        show: props.showLabels,
        position: 'outside', // Or 'inside'
        overflow: 'none',
        alignTo: 'none', // 'labelLine' | 'edge' | 'none'
        // distance: 10,       // Optional: Adjust distance from label line end
        formatter: (params) => {
          const { value } = params
          if (!props.unit) return value
          return props.unitPrefix
            ? `${props.unit} ${value}`
            : `${value}${props.unit}`
        },
        fontSize: '16px',
        fontWeight: 600,
      },
      labelLine: {
        show: true,
        length: 15,
        length2: 0,
        lineStyle: {
          color: '#000',
        },
      },
    },
  ]
})

const option = ref<ECOption>({
  textStyle: {
    fontFamily: 'Avenir Next LT Pro, Helvetica, Arial, sans-serif', // Set global font
  },

  grid: {
    containLabel: true, // Ensure labels are contained within the grid
  },

  media: [
    {
      query: { maxWidth: 300 },
      option: {
        series: [
          {
            radius: props.showLabels ? ['25%', '45%'] : ['45%', '80%'],
            label: { fontSize: '14px' },
          },
        ],
      },
    },
    {
      query: {
        minWidth: 300,
        maxWidth: 479,
      },
      option: {
        series: [
          {
            radius: props.showLabels ? ['35%', '60%'] : ['45%', '80%'],
            label: { fontSize: '14px' },
          },
        ],
      },
    },
    {
      option: {
        series: [
          {
            radius: props.showLabels ? ['40%', '65%'] : ['45%', '80%'],
            label: { fontSize: '16px' },
          },
        ],
      },
    },
  ],

  tooltip: {
    trigger: 'item',
    backgroundColor: '#F3F7FF',
    borderWidth: 0,
    padding: [10, 12],
    extraCssText: 'border-radius: 0; box-shadow: none; text-align: center;',
    formatter: (params) => {
      const { seriesName, value, name, color, seriesIndex } = params
      const formattedName = useFormatDate(name, locale)
      return `<span class="typo-body-lg">${formattedName}<br/><strong>${props.unitPrefix
        ? `${props.unit} ${value}`
        : `${value}${props.unit}`}</strong></span>`
    },
    textStyle: {
      color: '#000',
    },
  },

  series: series.value,
})

async function handleResize() {
  if (chartElement.value) {
    chartElement.value.resize() // Resize the chart instance
  }
}

const debouncedHandleResize = useDebounceFn(() => {
  handleResize()
}, 200)

onMounted(() => {
  window.addEventListener('resize', debouncedHandleResize)
})

onBeforeUnmount(() => {
  // Clean up event listener and chart instance
  window.removeEventListener('resize', debouncedHandleResize)
})
</script>
